import axios from "axios"
import { useEffect, useState } from "react"
import * as React from "react"

const handleJBError = () => {
  console.log(`Error occured fetching the job board`)
}

const useCareersAPI = () => {
  const CAREERS_URL = "https://api.greenhouse.io/v1/boards/fastly/embed/departments"
  const CAREERS_URL_JP = "https://api.greenhouse.io/v1/boards/fastlyjapan/embed/departments"
  const CAREERS_URL_FR = "https://api.greenhouse.io/v1/boards/fastlyfrance/embed/departments"
  const CAREERS_URL_MX = "https://api.greenhouse.io/v1/boards/fastlymexico/embed/departments"
  const CAREERS_URL_SK = "https://api.greenhouse.io/v1/boards/fastlysouthkorea/embed/departments"

  const [allDepartments, setAllDepartments] = useState([])
  const [departmentsWithOffers, setDepartmentsWithOffers] = useState([])
  const [locations, setLocations] = React.useState([])

  const deserializeResponse = (response) => {
    const mapDepartment = (department) => ({
      id: department.id,
      name: department.name,
      parentId: department.parent_id,
      childIds: department.child_ids,
      jobs: department.jobs,
    })
    const deserialized = response.departments.map(mapDepartment)

    return deserialized
  }

  const nameSort = (a, b) => {
    const nameA = a.name.toUpperCase()
    const nameB = b.name.toUpperCase()

    if (nameA < nameB) {
      return -1
    }

    if (nameA > nameB) {
      return 1
    }

    return 0
  }

  useEffect(() => {
    const fetchDepartments = async () => {
      const locationDepartments = []

      const hasNoParent = (department) => !department.parentId
      const hasJobs = (department) => department.jobs.length > 0
      const hasChild = (department) => department.childIds.length > 0
      const subDepartmentJobExists = (data, id) =>
        data.some((department) => id === department.id && hasJobs(department))

      const jobLocationExists = (job) => locationDepartments.includes(job.location["name"])
      const subDepartmentJobExistsInParent = (childId, parentId) => childId === parentId

      const getAllSubDepartmentJobs = (childId, data) =>
        data.reduce((acc, { id, jobs }) => {
          if (subDepartmentJobExistsInParent(id, childId)) {
            const jobsLocation = jobs.reduce((acc, job) => {
              if (!jobLocationExists(job)) {
                return [...acc, job.location["name"]]
              }
              return null
            }, [])
            return [...acc, ...jobsLocation]
          }
          return [...acc]
        }, [])

      const response = await axios(CAREERS_URL).catch(handleJBError)
      const responseJP = await axios(CAREERS_URL_JP).catch(handleJBError)
      const responseMX = await axios(CAREERS_URL_MX).catch(handleJBError)
      const responseFR = await axios(CAREERS_URL_FR).catch(handleJBError)
      const responseSK = await axios(CAREERS_URL_SK).catch(handleJBError)

      // start with the normal board
      const allData = response.data

      const localizedBoards = [responseJP, responseMX, responseFR, responseSK]

      localizedBoards.reduce((accu, board) => {
        // loop over each additional board
        board?.data.departments.forEach((jdepartment) => {
          // if there are jobs in the department
          if (jdepartment.jobs.length) {
            // find index of the matching one
            const targetDepartment = accu.departments.findIndex((d) => {
              return d.id === jdepartment.id
            })
            // TODO: copy department if not found
            if (targetDepartment !== -1) {
              // smush this job array onto the first set
              accu.departments[targetDepartment].jobs = allData.departments[targetDepartment].jobs.concat(
                jdepartment.jobs
              )
            }
          }
        })
        return accu
      }, allData)

      const data = deserializeResponse(allData)

      const departmentsWithOffersData = data
        .filter(hasNoParent)
        .filter(hasChild || hasJobs)
        // This filters out departments that haveJobs but don't have childIds,
        // which means they are a top-level department without sub-departments
        .filter(({ childIds }) => childIds.find((id) => subDepartmentJobExists(data, id)))

      const departmentsWithOffersDataIds = departmentsWithOffersData.map((dept) => dept.id)

      /*
      Exception for departments that are filtered above:
        [date added] [Department name] -> [Department ID]
        [date unknown] Office of CEO -> 69848
        [4/17/23] Program Management -> 25308
        [9/8/23] Law -> 1192
        [9/8/23] Product -> 56957
        [9/26/23] Business Development -> 88752
        [4/29/24] Global Network Infrastructure -> 150649
        [6/3/24] OCA -> 85894
      */
      const exceptionList = [56957, 69848, 25308, 1192, 88752, 150649, 85894]
      const corporateDevelopment = data
        .filter(hasNoParent)
        .filter(hasJobs)
        .filter((dept) => exceptionList.indexOf(dept.id) !== -1)
        .filter((dept) => !departmentsWithOffersDataIds.includes(dept.id))

      const allDepartmentsWithOffers = [...departmentsWithOffersData, ...corporateDevelopment].sort((a, b) =>
        nameSort(a, b)
      )

      const locationsList = allDepartmentsWithOffers.reduce((acc, { jobs, childIds }) => {
        const deparmentJobList = jobs.reduce((acc, job) => [...acc, job.location["name"]], [])
        const subDepartmentsJobList = childIds.reduce((acc, childId) => {
          const jobList = getAllSubDepartmentJobs(childId, data)
          return [...acc, ...jobList]
        }, [])
        return [...acc, ...deparmentJobList, ...subDepartmentsJobList]
      }, [])

      setAllDepartments(data)
      setDepartmentsWithOffers(allDepartmentsWithOffers)
      setLocations([...new Set(locationsList)].sort((a, b) => (a > b ? 1 : -1)))
    }
    fetchDepartments()
  }, [])

  return { departmentsWithOffers, locations, allDepartments }
}

export default useCareersAPI
